import { useEffect, useState } from 'react';
import { adminApi } from '../../utils/api';
import { Alert, Badge, Button, Modal, Spinner } from './shared';
import SyncConfigFormModal, { EMPTY_SYNC_CONFIG } from './SyncConfigFormModal';
import SyncRunModal from './SyncRunModal';

const RESOURCE_LABEL = {
    skill: 'skills',
    subagent: 'sub-agents',
    knowledge: 'knowledge files',
};


export default function SyncPickerModal({ open, onClose, resourceType, onSuccess }) {
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [runConfigId, setRunConfigId] = useState(null);

    const label = RESOURCE_LABEL[resourceType] || 'resources';

    useEffect(() => {
        if (!open) return;
        load();
    }, [open, resourceType]);

    const load = async () => {
        setLoading(true);
        setErr('');
        try {
            const rows = await adminApi.listSyncConfigs({ resourceType });
            setConfigs(Array.isArray(rows) ? rows : []);
        } catch (e) {
            setErr(e?.message || String(e));
        } finally {
            setLoading(false);
        }
    };

    const handleConfigSaved = async (payload) => {
        await adminApi.createSyncConfig(payload);
        setShowForm(false);
        await load();
    };

    return (
        <>
            <Modal open={open && !runConfigId} onClose={onClose} title={`Sync ${label} from a sync config`} size="lg">
                <Modal.Body>
                    {err && <Alert variant="danger">{err}</Alert>}
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-xs text-gray-500">Pick a configured source, or create a new one.</p>
                        <Button size="xs" onClick={() => setShowForm(true)}>+ New sync config</Button>
                    </div>

                    {loading ? (
                        <Spinner size="md" text="Loading..." center />
                    ) : configs.length === 0 ? (
                        <div className="text-center py-8 text-sm text-gray-400">
                            No sync configs for {label}. Create one to get started.
                        </div>
                    ) : (
                        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-96 overflow-y-auto">
                            {configs.map((c) => (
                                <div key={c.id} className="px-3 py-3 flex items-center justify-between hover:bg-gray-50">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-medium text-gray-900">{c.name}</span>
                                            <Badge variant="outline" className="text-xs">{c.id}</Badge>
                                            <Badge variant="info" className="text-xs">{c.provider}</Badge>
                                            <Badge variant="default" className="text-xs">env: {c.target_environment}</Badge>
                                        </div>
                                        <div className="text-xs text-gray-400 mt-0.5">
                                            credential: <code className="bg-gray-100 px-1 rounded">{c.credential_id}</code>
                                            {c.branch && <> · branch: <code className="bg-gray-100 px-1 rounded">{c.branch}</code></>}
                                            {(c.paths || []).length > 0 && <> · paths: {c.paths.join(', ')}</>}
                                        </div>
                                    </div>
                                    <Button size="xs" onClick={() => setRunConfigId(c.id)}>Sync</Button>
                                </div>
                            ))}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="ghost" onClick={onClose}>Close</Button>
                </Modal.Footer>
            </Modal>

            <SyncConfigFormModal
                open={showForm}
                onClose={() => setShowForm(false)}
                initial={{ ...EMPTY_SYNC_CONFIG, resource_type: resourceType }}
                isExisting={false}
                defaultResourceType={resourceType}
                onSave={handleConfigSaved}
            />

            <SyncRunModal
                open={!!runConfigId}
                configId={runConfigId}
                resourceType={resourceType}
                onClose={() => { setRunConfigId(null); load(); }}
                onSuccess={(msg) => { onSuccess?.(msg); }}
            />
        </>
    );
}
